import { pick } from 'lodash';
import { useCallback, useMemo } from 'react';
import { DbIdsPerModel, useViewer } from '../../components/common/ForgeViewer';
import { useSheetViewer } from '../../components/common/SheetViewer';
import { useSheetViewerV2 } from 'src/components/viewers/SheetsViewerV2/hooks/useSheetViewerV2';
import { useViewerMode, ViewerMode } from 'src/components/common/viewer-mode';

export type ColorizationContext = {
  colorSection: (
    colorFunc: (urn: string, dbId: number) => string,
    dbIdsPerModel: DbIdsPerModel
  ) => void;
  clearColors: () => void;
};

// Hook for colorization of elements, shapes and sheet shapes with a single interface
// Delegating calls to the right viewer type
export const useColorization = (): ColorizationContext => {
  const {
    colorSection: colorSectionForge,
    clearColors: clearColorsForge,
    shapesManager: { renderedShapes },
    loadedModels,
  } = useViewer();

  const {
    colorization: {
      colorSection: colorSectionSheetV1,
      clearColors: clearColorsSheetV1,
    },
    shapesManager: { renderedSheetShapes: renderedSheetShapesV1 },
  } = useSheetViewer();

  const {
    colorization: {
      colorSection: colorSectionSheetV2,
      clearColors: clearColorsSheetV2,
    },
    shapesManager: { renderedSheetShapes: renderedSheetShapesV2 },
  } = useSheetViewerV2();

  const { viewerMode } = useViewerMode();

  // Use the appropriate colorization manager based on viewer mode
  const colorSectionSheet = useMemo(() => {
    return viewerMode === ViewerMode.SheetsV2
      ? colorSectionSheetV2
      : colorSectionSheetV1;
  }, [viewerMode, colorSectionSheetV2, colorSectionSheetV1]);

  const clearColorsSheet = useMemo(() => {
    return viewerMode === ViewerMode.SheetsV2
      ? clearColorsSheetV2
      : clearColorsSheetV1;
  }, [viewerMode, clearColorsSheetV2, clearColorsSheetV1]);

  const renderedSheetShapes =
    viewerMode === ViewerMode.SheetsV2
      ? renderedSheetShapesV2
      : renderedSheetShapesV1;

  const forgeUrns = useMemo(
    () => [...Object.keys(renderedShapes), ...Object.keys(loadedModels)],
    [renderedShapes, loadedModels]
  );

  const sheetUrns = useMemo(
    () => Object.keys(renderedSheetShapes),
    [renderedSheetShapes]
  );

  const colorSection = useCallback(
    (
      colorFunc: (urn: string, dbId: number) => string,
      elements: DbIdsPerModel
    ) => {
      const forgeElements = pick(elements, forgeUrns);
      const sheetElements = pick(elements, sheetUrns);
      if (Object.keys(forgeElements).length > 0) {
        colorSectionForge(colorFunc, forgeElements);
      }
      if (Object.keys(sheetElements).length > 0) {
        colorSectionSheet(colorFunc, sheetElements);
      }
    },
    [colorSectionForge, colorSectionSheet, forgeUrns, sheetUrns]
  );

  const clearColors = useCallback(() => {
    clearColorsForge();
    clearColorsSheet();
  }, [clearColorsForge, clearColorsSheet]);

  return useMemo(
    () => ({
      colorSection,
      clearColors,
    }),
    [clearColors, colorSection]
  );
};
